import React from "react";
import QRCode from "qrcode";
import { reduxForm } from "redux-form";
import styled from "styled-components";
import hash from "object-hash";
import copy from "copy-to-clipboard";
import {
  Alert,
  AnchorButton,
  Button,
  Colors,
  Classes,
  Icon,
  Intent,
  InputGroup,
  Popover,
  Position,
  Tab2,
  Tabs2,
  Tooltip
} from "@blueprintjs/core";
import {
  Twitter as TwitterButton,
  Facebook as FacebookButton
} from "react-social-sharing";
import Byline from "./Byline";
import { connect } from "react-redux";
import { updateMap } from "app/map/actions";
import { computeBounds } from "app/map/utils";
import { createHocBase, capitalizeFirstLetter } from "app/utils";
import {
  compose,
  withHandlers,
  branch,
  renderNothing,
  withProps,
  withStateHandlers
} from "recompose";
import { withAuth } from "app/auth/hocs";
import { withObjectsConfig } from "app/topics/hocs";
import { withRatings } from "app/ratings/hocs";
import {
  QUERY_RATINGS,
  CREATE_RATING,
  DELETE_RATING
} from "app/ratings/constants";
import LocationFeedback from "app/feedback/LocationFeedback";
import { withAlertState } from "app/hocs";
import Comments from "app/comments/Comments";
import SimpleCommentCount from "app/comments/SimpleCommentCount";
import { withCommentCount } from "app/comments/hocs";
import { injectIntl, FormattedMessage, FormattedHTMLMessage } from "react-intl";

const Panel = styled.div.attrs({
  className: ""
})``;

const TextPanel = props => {
  const { title, text } = props;
  return (
    <Panel>
      <h5>{title}</h5>
      {text}
    </Panel>
  );
};

const ObjWrapper = styled.div`
  display: flex;
  flex-direction: row;
  border: 1px solid ${Colors.GRAY5};
  border-radius: 5px;
  margin: 1ex 0;
  padding: 1ex;
  background-color ${Colors.LIGHT_GRAY5};
  & .clickable {
  cursor: pointer;
  }
  & .clickable:hover {
    color: ${Colors.BLUE3}
  }
  & > div {
    flex: 1;
  }
  & .location-feedback {
    margin-top: 1ex;
    border-top: 1px solid #ccc;
    padding-top: 1ex;
  }
`;

const InputEntry = styled.div`
  padding: 0.2rem;
`;

const SocialButtonWrapper = styled.div`
  display: inline-block;
  & > a {
    margin: 0.1rem;
  }
`;

const clickable = compose(
  connect(
    null,
    dispatch => ({
      updateMap: options => dispatch(updateMap(options))
    })
  ),
  withHandlers({
    onClick: props => () => {
      const {
        updateMap,
        object: { location }
      } = props;
      const bounds = computeBounds(location);
      updateMap({ bounds });
    }
  })
);

const Obj = clickable(props => {
  const { object, onClick } = props;
  return (
    <ObjWrapper>
      <div>
        <div className="clickable" onClick={onClick}>
          <Icon iconName="map-marker" /> <strong>{object.title}</strong>
          {Object.keys(object.inputs || {}).map(key => (
            <InputEntry key={key}>
              {capitalizeFirstLetter(key)}:{" "}
              {capitalizeFirstLetter(object.inputs[key])}
            </InputEntry>
          ))}
        </div>
        <LocationFeedback
          locationObject={object}
          className="location-feedback"
        />
      </div>
    </ObjWrapper>
  );
});

const ObjectsPanel = withObjectsConfig(props => {
  const { objects = [], objectsConfig } = props;
  return (
    <Panel>
      <h5>
        <FormattedMessage id="proposal.objects" defaultMessage="Objects" />
      </h5>
      <div>
        {objects.map(object => (
          <Obj object={object} key={object.id} objectsConfig={objectsConfig} />
        ))}
      </div>
    </Panel>
  );
});

const CommentsPanel = props => (
  <Panel>
    <h5>
      <FormattedMessage id="proposal.comments" defaultMessage="Comments" />
    </h5>
    <Comments {...props} />
  </Panel>
);

const Tabs3 = styled(Tabs2)`
  background-color: white;
  padding: 1ex;
  border-radius: 5px;
  margin-bottom: 1em;
`;

const SpecialButtonGroup = styled.div.attrs({
  className: "pt-button-group pt-minimal"
})`
  margin: 10px 0;
  background-color: white;
  width: 100%;
  border: 3px solid white;
  border-radius: 5px;
  display: block !important;
  overflow: auto;
`;

const likeProposal = props => () => {
  const { createRating, queryRatings, proposalId, userId } = props;
  createRating({ proposalId, userId }).then(() => {
    const id = hash({ proposalId });
    const ratingParams = { id, proposalId };
    queryRatings(ratingParams);
  });
};

const unlikeProposal = props => () => {
  const {
    deleteRating,
    queryRatings,
    myRating: { _id: ratingId },
    proposalId
  } = props;

  deleteRating({ ratingId }).then(() => {
    const id = hash({ proposalId });
    const ratingParams = { id, proposalId };
    queryRatings(ratingParams);
  });
};

const enhanceLikeButton = compose(
  withRatings,
  branch(props => !props.ratings, renderNothing),
  withProps(props => {
    const { ratings, userId } = props;
    const result = ratings.result;
    return {
      loading:
        ratings[QUERY_RATINGS.PENDING] === true ||
        ratings[CREATE_RATING.PENDING] === true ||
        ratings[DELETE_RATING.PENDING] === true,
      myRating: result
        ? result.filter(rating => rating.userId === userId)[0]
        : undefined
    };
  }),
  withHandlers({ likeProposal, unlikeProposal })
);

const LikeButton = enhanceLikeButton(props => {
  const {
    loading,
    likeProposal,
    unlikeProposal,
    myRating,
    isAuthenticated,
    intl
  } = props;
  const liked = Boolean(myRating);
  const intent = liked ? Intent.SUCCESS : undefined;
  const onClick = liked ? unlikeProposal : likeProposal;
  return (
    <AnchorButton
      onClick={onClick}
      iconName="thumbs-up"
      text={intl.formatMessage({ id: "proposal.like", defaultMessage: "Like" })}
      loading={loading}
      active={liked}
      intent={intent}
      disabled={!isAuthenticated}
      style={{ float: "left" }}
    />
  );
});

const WithAuth = withAuth(({ children, ...props }) => children(props));

const DeleteButton = withAlertState(props => {
  const { onClick, openAlert, closeAlert, isOpen, intl, ...rest } = props;
  return (
    <div style={{ display: "inline-block", float: "left" }}>
      <AnchorButton {...rest} onClick={openAlert} />
      <Alert
        isOpen={isOpen}
        intent={Intent.DANGER}
        confirmButtonText={intl.formatMessage({
          id: "text.delete",
          defaultMessage: "Delete"
        })}
        cancelButtonText={intl.formatMessage({
          id: "text.cancel",
          defaultMessage: "Cancel"
        })}
        onConfirm={onClick}
        onCancel={closeAlert}
      >
        <FormattedHTMLMessage
          id="proposal.delete.prompt"
          defaultMessage="Are you sure you want to <strong>delete</strong> this proposal?"
        />
      </Alert>
    </div>
  );
});

const PublishButton = withAlertState(props => {
  const { onClick, openAlert, closeAlert, isOpen, intl, ...rest } = props;
  return (
    <div>
      <AnchorButton {...rest} onClick={openAlert} />
      <Alert
        isOpen={isOpen}
        intent={Intent.SUCCESS}
        confirmButtonText={intl.formatMessage({
          id: "proposal.publish.text",
          defaultMessage: "Publish"
        })}
        cancelButtonText={intl.formatMessage({
          id: "text.cancel",
          defaultMessage: "Cancel"
        })}
        onConfirm={() => {
          onClick();
          closeAlert();
        }}
        onCancel={closeAlert}
      >
        <FormattedHTMLMessage
          id="proposal.publish.prompt"
          defaultMessage="Are you sure you want to <strong>publish</strong> this proposal?"
        />
      </Alert>
    </div>
  );
});

export const withCopyState = compose(
  withProps(props => {
    let baseUrl = window.location.href;
    const parts = baseUrl.split("/");
    baseUrl = `${parts[0]}//${parts[2]}`;
    return { url: `${baseUrl}${props.path}` };
  }),
  withStateHandlers(
    { done: false },
    {
      copyUrl: (_, props) => () => {
        const input = document.getElementById("copyUrlInput");
        input.focus();
        input.select();
        copy(props.url);
        return { done: true };
      }
    }
  ),
  Component =>
    class extends createHocBase(Component) {
      componentDidMount() {
        const input = document.getElementById("copyUrlInput");
        input.focus();
        input.select();
      }
    }
);

const ShareUrlStyle = styled.div`
  & .copy-url-input {
    margin: 12px 0;
  }
  & h5 {
    font-size: 16px;
    margin: 0 0 12px 0;
  }
  & img {
    border: 2px solid ${Colors.GRAY5};
    border-radius: 16px;
    display: block;
  }
`;

const ShareUrl = withCopyState(props => {
  const { done, copyUrl, url, intl } = props;
  let qrSrc = "data:image/svg+xml;base64,";
  QRCode.toString(props.url, { scale: 7 }, (error, qrString) => {
    if (error) throw error;
    qrSrc = `${qrSrc}${window.btoa(qrString)}`;
  });
  return (
    <ShareUrlStyle>
      <h5>
        <FormattedMessage id="proposal.url" defaultMessage="Proposal URL" />
      </h5>
      {done && (
        <p style={{ color: Colors.GREEN3 }}>
          <Icon iconName="small-tick" />
          <FormattedMessage
            id="tooltip.clipboard.copy"
            defaultMessage="Copy to clipboard"
          />" "}
        </p>
      )}
      <div className="copy-url-input">
        <InputGroup
          type="text"
          defaultValue={url}
          id="copyUrlInput"
          rightElement={
            <Tooltip
              content={intl.formatMessage({
                id: "tooltip.clipboard.copy",
                defaultMessage: "Copy to clipboard"
              })}
            >
              <Button
                className={Classes.MINIMAL}
                iconName="clipboard"
                onClick={copyUrl}
              />
            </Tooltip>
          }
        />
      </div>
      <h5>
        <FormattedMessage id="proposal.share.qrcode" defaultMessage="QRCode" />
      </h5>
      <img
        alt={intl.formatMessage({
          id: "proposal.share.qrcode",
          defaultMessage: "QRCode"
        })}
        src={qrSrc}
      />
    </ShareUrlStyle>
  );
});

const ShareButton = props => {
  const { proposalPath, intl, ...rest } = props;
  return (
    <Popover
      popoverClassName="pt-popover-content-sizing"
      position={Position.BOTTOM}
    >
      <AnchorButton {...rest} />
      <ShareUrl path={proposalPath} intl={intl} />
    </Popover>
  );
};

const Buttons = props => {
  const {
    isDraft,
    onEditClick,
    onDeleteClick,
    proposalId,
    proposalPath,
    // canRead = false,
    canDelete = false,
    intl
  } = props;
  return (
    <WithAuth>
      {props => {
        const {
          auth: { isAuthenticated, userId }
        } = props;
        let isAdmin = false;
        if (isAuthenticated) {
          ({
            auth: {
              user: { admin: isAdmin }
            }
          } = props);
        }
        return (
          <SpecialButtonGroup>
            {!isDraft && (
              <LikeButton
                isAuthenticated={isAuthenticated}
                proposalId={proposalId}
                userId={userId}
                intl={intl}
              />
            )}
            {!isDraft && (
              <ShareButton
                iconName="share"
                text={intl.formatMessage({
                  id: "proposal.share",
                  defaultMessage: "Share"
                })}
                proposalPath={proposalPath}
                intl={intl}
              />
            )}
            {isDraft && (
              <AnchorButton
                iconName="edit"
                text={intl.formatMessage({
                  id: "proposal.editdraft",
                  defaultMessage: "Edit draft"
                })}
                disabled={!isAuthenticated}
                onClick={onEditClick}
              />
            )}
            <DeleteButton
              iconName="trash"
              text={intl.formatMessage({
                id: "text.delete",
                defaultMessage: "Delete"
              })}
              disabled={!canDelete && !isDraft && !isAdmin}
              onClick={onDeleteClick}
              intent={Intent.DANGER}
              intl={intl}
            />
            <SocialButtonWrapper>
              <TwitterButton small link={window.location.href} />
            </SocialButtonWrapper>
            <SocialButtonWrapper>
              <FacebookButton small link={window.location.href} />
            </SocialButtonWrapper>
          </SpecialButtonGroup>
        );
      }}
    </WithAuth>
  );
};

const PublishForm = reduxForm()(props => {
  const { handleSubmit, submit, submitting, intl } = props;
  return (
    <form onSubmit={handleSubmit}>
      <WithAuth>
        {props => {
          const {
            auth: { isAuthenticated }
          } = props;
          return (
            <PublishButton
              iconName="endorsed"
              text={intl.formatMessage({
                id: "proposal.publish",
                defaultMessage: "Publish proposal"
              })}
              type="submit"
              loading={submitting}
              disabled={!isAuthenticated}
              onClick={submit}
              intl={intl}
            />
          );
        }}
      </WithAuth>
    </form>
  );
});

const enhanceCommentsTitle = compose(
  withProps(props => {
    const { proposalId: resourceId, isDraft } = props;
    const resourceType = isDraft ? "Draft" : "Proposal";
    return { resourceId, resourceType };
  }),
  withCommentCount
);

let CommentsTitle = enhanceCommentsTitle(props => {
  return (
    <span>
      <FormattedMessage id="proposal.comments" defaultMessage="Comments" />{" "}
      <SimpleCommentCount commentCount={props.commentCount} />
    </span>
  );
});

let ProposalView = props => {
  const {
    proposalId,
    proposal,
    topicId,
    onEditClick,
    onDeleteClick,
    onPublishClick,
    intl
  } = props;
  const isLondon = (topicId || "").match(/rbkc/);
  return (
    <div>
      <p>{proposal.brief}</p>
      <Byline proposal={proposal} />
      <Buttons
        proposalPath={proposal.ui.path}
        proposalId={proposalId}
        isDraft={proposal.ui.isDraft}
        onEditClick={onEditClick}
        onDeleteClick={onDeleteClick}
        proposalOwner={proposal.userId}
        canRead={proposal.permissions && proposal.permissions.read}
        canDelete={proposal.permissions && proposal.permissions.delete}
        intl={intl}
      />
      <Tabs3 animate={false}>
        {!isLondon && (
          <Tab2
            id="objects"
            title={intl.formatMessage({
              id: "proposal.objects",
              defaultMessage: "Objects"
            })}
            panel={
              <ObjectsPanel
                objects={proposal.content.objects}
                topicId={topicId}
              />
            }
          />
        )}
        {!isLondon && (
          <Tab2
            id="description"
            title={intl.formatMessage({
              id: "proposal.description",
              defaultMessage: "Description"
            })}
            panel={
              <TextPanel
                title={intl.formatMessage({
                  id: "proposal.description",
                  defaultMessage: "Description"
                })}
                text={proposal.description}
              />
            }
          />
        )}
        <Tab2
          id="comments"
          title={
            <CommentsTitle
              proposalId={proposalId}
              isDraft={proposal.ui.isDraft}
            />
          }
          panel={
            <CommentsPanel
              proposalId={proposalId}
              isDraft={proposal.ui.isDraft}
              intl={intl}
            />
          }
        />
      </Tabs3>
      {proposal.ui.isDraft && (
        <PublishForm
          onSubmit={onPublishClick}
          form={`publish${proposalId}`}
          intl={intl}
        />
      )}
    </div>
  );
};

export default injectIntl(ProposalView);



// WEBPACK FOOTER //
// ./src/app/proposals/ProposalView.js